import {
  Box,
  Button,
  Circle,
  Divider,
  Flex,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  formatDollars,
  formatFullDate,
  formatJsDate,
  formatPeriodShort,
  statusColor,
} from '~/backup/shared/format';
import {
  usePortalBusServicingCycleQuery,
  usePortalBusServicingCyclesForDealQuery,
} from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';

// -- cycle stepper --

type StepState = 'complete' | 'current' | 'pending';

type CycleStep = {
  label: string;
  detail: string;
  complete: boolean;
};

const STEP_DOT_COLOR: Record<StepState, string> = {
  complete: colors.green[400],
  current: colors.blue[400],
  pending: colors.gray[200],
};

function StepDot({ state }: { state: StepState }): React.ReactElement {
  return (
    <Circle
      size="1.5rem"
      bg={state === 'pending' ? 'transparent' : STEP_DOT_COLOR[state]}
      borderWidth={state === 'pending' ? '2px' : '0'}
      borderColor={colors.gray[200]}
      color="white"
      fontSize="0.85rem"
    >
      {state === 'complete' ? '✓' : ''}
    </Circle>
  );
}

function CycleStepper({ steps }: { steps: CycleStep[] }): React.ReactElement {
  // The first not-yet-complete step is the "current" one.
  const currentIndex = steps.findIndex((s) => !s.complete);

  return (
    <Flex align="flex-start" gap="0">
      {steps.map((step, i) => {
        const getState = (): StepState => {
          if (step.complete) return 'complete';
          return i === currentIndex ? 'current' : 'pending';
        };
        const state: StepState = getState();
        const isLast = i === steps.length - 1;
        return (
          <Flex key={step.label} flex={isLast ? '0 0 auto' : '1'} minW="0">
            <VStack
              align="flex-start"
              spacing="0.35rem"
              minW="0"
              flexShrink={1}
            >
              <StepDot state={state} />
              <Text fontWeight={600} fontSize="0.85rem">
                {step.label}
              </Text>
              <Text color={colors.gray[500]} fontSize="0.75rem">
                {step.detail}
              </Text>
            </VStack>
            {!isLast && (
              <Box
                flex="1"
                height="2px"
                bg={step.complete ? colors.green[400] : colors.gray[200]}
                mt="0.7rem"
                mx="0.5rem"
              />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

// -- reconcile checks --

const CHECK_COLOR: Record<string, string> = {
  pass: colors.green[400],
  fail: colors.red[400],
  skipped: colors.gray[400],
};

function CheckRow({
  name,
  status,
  detail,
}: {
  name: string;
  status: string;
  detail: string;
}): React.ReactElement {
  const color = CHECK_COLOR[status] ?? colors.gray[400];
  return (
    <Flex align="baseline" gap="0.75rem" py="0.4rem">
      <Text color={color} fontWeight={600} fontSize="0.75rem" minW="4rem">
        {status.toUpperCase()}
      </Text>
      <Box>
        <Text fontWeight={500}>{name}</Text>
        {detail && (
          <Text color={colors.gray[500]} fontSize="0.8rem">
            {detail}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

// -- small labelled value --

function Field({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}): React.ReactElement {
  return (
    <VStack align="flex-start" spacing="0.15rem">
      <Text
        color={colors.gray[500]}
        fontSize="0.7rem"
        fontWeight={600}
        letterSpacing="0.02em"
      >
        {label}
      </Text>
      <Text fontWeight={500} color={valueColor}>
        {value}
      </Text>
    </VStack>
  );
}

// -- component --

export const DealOverviewView = (): React.ReactElement => {
  const { implementationId } = useParams<{ implementationId: string }>();

  // All cycles for the deal (newest reporting period first) drive the header +
  // recent-cycles list; the latest one is shown in full detail below.
  const {
    data: listData,
    loading: listLoading,
    error: listError,
  } = usePortalBusServicingCyclesForDealQuery({
    variables: { implementationId: implementationId ?? '' },
    skip: !implementationId,
    fetchPolicy: 'cache-and-network',
  });

  const cycles = listData?.portalBusServicingCycles ?? [];
  const latest = cycles[0] ?? null;

  // The latest cycle's full detail (stepper / reconcile / certificate download).
  const {
    data: detailData,
    loading: detailLoading,
  } = usePortalBusServicingCycleQuery({
    variables: { servicingCycleId: latest?.id ?? '' },
    skip: !latest,
    fetchPolicy: 'cache-and-network',
  });

  if ((listLoading && !listData) || (latest && detailLoading && !detailData)) {
    return (
      <Flex justify="center" align="center" minH="20rem">
        <Spinner />
      </Flex>
    );
  }

  const cycle = detailData?.portalBusServicingCycle;

  if (listError || !latest || !cycle) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          Deal not found
        </Typography>
        <Text color={colors.gray[500]}>
          This deal has no servicing cycles available on your account.
        </Text>
      </Flex>
    );
  }

  const steps: CycleStep[] = [
    {
      label: 'Data Received',
      // A BUS cycle reconciles the data tape against the MSR, so both inputs are
      // required before this step completes; surface each leg so a still-pending
      // MSR reads as "MSR —" rather than looking stuck for no visible reason.
      detail: `Tape ${formatJsDate(cycle.tapeReceivedAt)} · MSR ${formatJsDate(
        cycle.msrReceivedAt,
      )}`,
      complete: cycle.tapeReceivedAt !== null && cycle.msrReceivedAt !== null,
    },
    {
      label: 'Reconciled',
      detail: cycle.reconcileResult
        ? cycle.reconcileResult.status.toUpperCase()
        : 'Pending',
      complete: cycle.reconcileResult !== null,
    },
    {
      label: 'Certificate Issued',
      detail: formatJsDate(cycle.certificateIssuedAt),
      complete: cycle.certificateIssuedAt !== null,
    },
    {
      label: 'Certificate Signed',
      detail: formatJsDate(cycle.certificateSignedAt),
      complete: cycle.certificateSignedAt !== null,
    },
  ];

  const reconcile = cycle.reconcileResult;
  const canDownload = cycle.certificateDownloadUrl !== null;

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="2rem"
    >
      {/* Header */}
      <Flex justify="space-between" align="baseline" wrap="wrap" gap="0.5rem">
        <Box>
          <Typography variant="page-title" trim>
            {cycle.creditFacilityName}
          </Typography>
          <Text color={colors.gray[500]} fontSize="0.9rem">
            {cycle.organizationName} · {cycle.implementationName} · Latest cycle{' '}
            {formatFullDate(cycle.reportingPeriod)}
          </Text>
          <Link
            as={RouterLink}
            to={`/deals/${implementationId ?? ''}/files`}
            fontSize="0.8rem"
            color={colors.blue[400]}
            fontWeight={500}
          >
            View all deliverables →
          </Link>
        </Box>
        <Button
          as="a"
          href={cycle.certificateDownloadUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          isDisabled={!canDownload}
          colorScheme="blue"
          size="sm"
        >
          {canDownload ? 'Download Certificate' : 'Certificate Not Ready'}
        </Button>
      </Flex>

      {/* Cycle stepper */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderColor={colors.gray[100]}
        p="1.5rem"
      >
        <CycleStepper steps={steps} />
      </Box>

      {/* Summary fields */}
      <SimpleGrid columns={{ base: 2, md: 4 }} gap="1rem">
        <Field label="CYCLE STATUS" value={cycle.cycleStatus} />
        <Field label="APPROVAL" value={cycle.approvalStatus} />
        <Field
          label="TAPE ROWS"
          value={cycle.tapeRowCount?.toLocaleString() ?? '—'}
        />
        <Field
          label="TAPE PRINCIPAL"
          value={formatDollars(cycle.tapeTotalPrincipal)}
        />
      </SimpleGrid>

      {/* Reconciliation */}
      {reconcile && (
        <Box
          borderWidth="1px"
          borderRadius="md"
          borderColor={colors.gray[100]}
          p="1.5rem"
        >
          <Flex justify="space-between" align="baseline" mb="1rem">
            <Typography variant="section-title">Reconciliation</Typography>
            <Text fontWeight={600} color={statusColor(reconcile.status)}>
              {reconcile.status.toUpperCase()}
            </Text>
          </Flex>

          <SimpleGrid columns={{ base: 2, md: 4 }} gap="1rem" mb="1rem">
            <Field
              label="LOAN COUNT"
              value={reconcile.controlTotals.loanCount.toLocaleString()}
            />
            <Field
              label="TOTAL PRINCIPAL"
              value={formatDollars(reconcile.controlTotals.totalPrincipal)}
            />
            {reconcile.msrValues && (
              <>
                <Field
                  label="MSR ENDING BALANCE"
                  value={formatDollars(reconcile.msrValues.endingPoolBalance)}
                />
                <Field
                  label="SERVICING FEE"
                  value={formatDollars(reconcile.msrValues.servicingFee)}
                />
              </>
            )}
          </SimpleGrid>

          <Divider mb="0.5rem" />

          <VStack align="stretch" spacing="0" divider={<Divider />}>
            {reconcile.checks.map((check) => (
              <CheckRow
                key={check.name}
                name={check.name}
                status={check.status}
                detail={check.detail}
              />
            ))}
          </VStack>

          <Text color={colors.gray[400]} fontSize="0.7rem" mt="1rem">
            {reconcile.provenance}
          </Text>
        </Box>
      )}

      {/* Recent cycles */}
      {cycles.length > 1 && (
        <Box>
          <Typography variant="section-title">Recent Cycles</Typography>
          <VStack align="stretch" spacing="0" divider={<Divider />} mt="0.5rem">
            {cycles.map((c) => (
              <Flex
                key={c.id}
                justify="space-between"
                align="center"
                gap="1rem"
                py="0.6rem"
              >
                <Text fontWeight={500} minW="6rem">
                  {formatPeriodShort(c.reportingPeriod)}
                </Text>
                <Text
                  fontSize="0.8rem"
                  fontWeight={600}
                  color={statusColor(c.reconcileStatus ?? '')}
                >
                  {(c.reconcileStatus ?? 'pending').toUpperCase()}
                </Text>
                <Text fontSize="0.8rem" color={colors.gray[500]}>
                  {c.certificateStatus
                    ? `Cert ${c.certificateStatus}`
                    : 'No certificate'}
                </Text>
                <Text fontSize="0.8rem" color={colors.gray[500]}>
                  {(c.tapeRowCount ?? 0).toLocaleString()} rows ·{' '}
                  {formatDollars(c.tapeTotalPrincipal)}
                </Text>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}
    </Flex>
  );
};
